import type { Dictionary } from "./hy";

export const en: Dictionary = {
  meta: { code: "en", label: "Eng", name: "English", ogLocale: "en_US" },
  common: {
    brand: "Global IT Academy",
    details: "Details",
    learnMore: "Learn more",
    registerNow: "Register now",
    loading: "Loading...",
    visit: "Visit",
    close: "Close",
    add: "Add",
  },
  nav: {
    home: "Home",
    news: "News",
    admission: "Admission",
    services: "Services",
    contact: "Contact",
    login: "Log in",
    logout: "Log out",
    signOut: "Sign out",
    admin: "Admin",
    lecturer: "Lecturer",
    student: "Student",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    language: "Language",
    theme: "Theme",
    switchToLight: "Switch to light theme",
    switchToDark: "Switch to dark theme",
    light: "Light",
    dark: "Dark",
  },
  hero: {
    badge: "Admission is open",
    closed: "Admission is closed",
    until: "until",
    titleLead: "IT courses in Armenia:",
    titleAccent: "programming, cybersecurity and AI",
    subtitle: "Global IT Academy — cybersecurity and programming academy",
    description:
      "Modern IT training for pre-conscription youth and all beginners: on-site and online, in a group or one-on-one.",
    ctaRegister: "Register now",
    ctaAdvisor: "AI Advisor",
    chips: ["On-site and online", "Group or individual", "Certificate verified online", "Employment support"],
    admission: "ADMISSION",
    admissionHint: "Seats are limited",
    featuredLabel: "TOP 2026",
    featuredTitle: "Applied AI, step by step",
    featuredMeta: "3 days × 2 hours · on-site · group",
    featuredPrice: "45,000 AMD",
    directions: "tracks",
    directionsHint: "Web, AI, Cyber, Kids, QA",
    marqueeLabel: "Technologies we teach",
    scrollHint: "Explore courses",
  },
  countdown: { days: "Days", hours: "Hrs", minutes: "Min", seconds: "Sec" },
  courses: {
    kicker: "Learning tracks",
    title: "TOP Courses",
    copy: "Pick your specialization and start your IT career with us.",
    all: "All courses",
    details: "Details",
    badges: { top2026: "TOP 2026", top: "TOP", new: "NEW" },
    items: {
      aiTeacher: { title: "Applied AI", subtitle: "step by step (3 days)" },
      cyber: { title: "Cybersecurity", subtitle: "Protect the digital world" },
      web: { title: "Web Programming", subtitle: "Frontend and Backend technologies" },
      ai: { title: "Artificial Intelligence", subtitle: "AI tools and machine learning" },
      kids: { title: "Programming for Kids", subtitle: "Logic, games and creative thinking" },
      qa: { title: "QA Testing", subtitle: "Manual and automation testing" },
    },
  },
  faculties: {
    title: "Faculties",
    copy: "Choose your area of interest and start your professional growth",
    loading: "Loading...",
    error: "Failed to load data",
    soon: "Courses coming soon",
    status: {
      new: "New",
      discount: "Discount",
      action: "Promo",
      enrollment: "Enrolling",
      active: "Active",
      draft: "Draft",
      full: "Full",
      completed: "Completed",
    },
  },
  stories: { title: "Stories", add: "Add", empty: "No stories yet", createFirst: "Create the first story" },
  news: {
    kicker: "Global IT Academy",
    title: "News",
    type: { event: "Event", announcement: "Announcement", news: "News" },
    details: "Details",
    readFull: "Read the full story",
    viewAll: "View all news",
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  },
  partners: {
    title: "Our partners",
    copy: "We are proud of the partners who help us deliver quality education",
    visit: "Visit",
  },
  whyUs: {
    kicker: "Why Global IT Academy",
    title: "Training built around your outcome",
    copy: "A modern program, a convenient format and support — from the first lesson to your first job.",
    items: [
      { title: "A format that fits you", text: "On-site in Yerevan or online, in a group or one-on-one — choose what works for you." },
      { title: "AI career advisor", text: "Our free AI advisor helps you pick the right track in a few minutes." },
      { title: "Verifiable certificate", text: "The certificate you receive can be verified online with its control number." },
      { title: "Flexible payment", text: "Visa, Mastercard, Arca, Idram, gift cards and support with social expense refunds." },
      { title: "Career and partners", text: "Employment support and collaboration with partner organizations." },
      { title: "Always at hand", text: "Install the site as an app and get notified about new courses and events." },
    ],
  },
  faq: {
    kicker: "Frequently asked questions",
    title: "What people ask most often",
    copy: "Short answers to make the decision easy.",
    items: [
      {
        q: "In what format are the courses held?",
        a: "On-site (Yerevan) and online, in groups and individually. You choose the format when you register.",
      },
      {
        q: "How long do the courses last?",
        a: "Basic stage — 6 months, pre-conscription — 9 months, academic — 18 months. There are also short intensives, such as the three-day Applied AI training.",
      },
      {
        q: "How do I register?",
        a: "Click “Register now”, fill in the form and we will contact you. You can also message us on Telegram or call +374 98 960959.",
      },
      {
        q: "Do you issue a certificate?",
        a: "Yes. A certificate is issued on completion, and its authenticity can be checked on the “Certificate verification” page using the control number.",
      },
      {
        q: "How can I pay?",
        a: "With Visa, Mastercard, Arca cards and Idram. Gift cards are also available.",
      },
      {
        q: "Which track should I choose if I have not decided yet?",
        a: "Use the free AI advisor on the home page: after a few questions it will suggest a track that fits you.",
      },
    ],
  },
  contact: {
    title: "Ready to start your IT journey?",
    copy: "Hurry up and register: only a few days are left until admission closes, and seats are limited.",
    telegram: "Telegram",
    telegramText: "Message us on Telegram",
    phone: "Phone",
    phoneText: "Give us a call",
    social: "Social media",
    socialText: "Follow us",
    app: {
      title: "Web app",
      text: "You can install our site on your phone as a web app — no App Store or Google Play needed",
      f1: "Quick access from the home screen",
      f2: "Feels like a native app",
      f3: "Automatic updates",
      install: "Install the app",
      iosAlert: "Open this site in Safari, tap the Share button and choose \"Add to Home Screen\"",
      androidAlert: "Open this site in Chrome, tap the Menu (⋮) button and choose \"Install app\" or \"Add to Home screen\"",
      installShort: "Install",
      webInstruction: "Open this site in Chrome and click the Install icon in the address bar",
    },
  },
  contactPage: {
    kicker: "Contact",
    title: "Get in touch",
    subtitle: "Questions, consultation or enrollment — pick the way that suits you and we'll get back to you as soon as possible.",
    phone: "Phone",
    phoneAction: "Call us",
    telegramAction: "Message on Telegram",
    whatsappAction: "Message on WhatsApp",
    viberAction: "Message on Viber",
    youtubeAction: "Subscribe",
    linkedinAction: "Follow us",
    formTitle: "Enroll in a course",
    formText: "Leave an application and our team will contact you.",
    register: "Apply now",
  },
  newsletter: {
    title: "Stay in the loop",
    text: "Be the first to hear about new courses and events",
    placeholder: "Your email address",
    subscribe: "Subscribe",
    subscribing: "Subscribing...",
    errorTitle: "Error",
    invalid: "Please enter a valid email address",
    alreadyTitle: "Already subscribed",
    alreadyText: "This email is already subscribed to our news",
    successTitle: "Success!",
    resubscribed: "You have re-subscribed to our news",
    subscribed: "You have successfully subscribed to our news",
    genericError: "Something went wrong while subscribing",
  },
  footer: {
    rights: "© 2026 Global IT Academy. All rights reserved.",
    tagline: "Programming, cybersecurity and AI courses in Armenia",
    partnership: "Partnership",
    giftCards: "Gift cards",
    certificate: "Certificate verification",
    socialRefund: "Social expense refund application",
    privacy: "Privacy policy",
    refund: "Refund and cancellation policy",
    navTitle: "Site",
    coursesTitle: "Courses",
    legalTitle: "Useful",
  },
  seo: {
    homeTitle: "Global IT Academy — IT courses in Armenia",
    homeDescription:
      "Professional programming, cybersecurity and AI courses in Armenia — on-site and online. Web, Python, Java, C#, C++, QA. Enroll at Global IT Academy.",
  },
  newsSection: {
    title: "News",
    description: "Latest news, events and announcements from Global IT Academy",
    empty: "There are no news items yet",
    details: "Details",
    openLink: "Open link",
    typeEvent: "Event",
    typeAnnouncement: "Announcement",
    typeNews: "News",
  },
  newsDetail: {
    home: "Home",
    featured: "Featured",
    register: "Register for the event",
    registerShort: "Register",
    eventDetails: "Event details",
    dateTime: "Date and time",
    location: "Location",
    map: "Map",
    deadline: "Registration deadline",
    urgent: "Urgent announcement",
    validUntil: "Valid until:",
    description: "Description",
    showLess: "Show less",
    readMore: "Read more",
    speakers: "Speakers",
    agenda: "Agenda",
    speakersLabel: "Speaker(s):",
    externalLink: "Read more",
    notFound: "News item not found",
    videoError: "The video cannot be loaded. Please try again later.",
    home2: "Home",
    newsCrumb: "News",
  },
  admissionSection: {
    loading: "Loading...",
    title: "Admission",
    subtitle: "High-quality IT education with modern technologies and experienced professionals",
    datesTitle: "Key dates",
    deadlineLabel: "Applications are accepted until",
    startLabel: "Course start",
    durationTitle: "Course duration",
    month: "months",
    durationBase: "basic",
    durationPre: "pre-conscription",
    durationAcademic: "academic",
    monthLessons: "1 month = 12 lessons",
    scheduleTitle: "Days and hours",
    daysOdd: "Mon. Wed. Fri.",
    daysEven: "Tue. Thu. Sat.",
    dependsOnGroup: "(Depends on the group)",
    priceTitle: "Price",
    group: "Group",
    individual: "Individual",
    monthly: "per month",
    currency: "AMD",
    formatsTitle: "Learning formats",
    inPerson: "On-site",
    online: "Online",
    timetableTitle: "Timetable",
    weekly: "Per week",
    weeklyValue: "3 times",
    lessonLength: "Lesson duration",
    lessonLengthValue: "1.5 - 2 hours",
    ageGroup: "Age group",
    ageGroupValue: "from 15 years",
    bonusesTitle: "Bonuses and benefits",
    bonus1: "1 month of free practice",
    bonus2: "Trilingual graduation certificate",
    bonus3: "Endorsed by the Ministry of Education, Science, Culture and Sport of Armenia",
    bonus4: "Option to reclaim up to 100,000 AMD of tuition costs.",
  },
  servicesSection: {
    title: "Our services",
    subtitle: "We offer a wide range of services to digitalise your business",
    learnMore: "Learn more",
    order: "Order",
  },
};
